import { CalendarClock, Landmark, ListOrdered, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Section } from '@/components/common/Blocks'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { fmtDate, fmtMoney } from '../../../shared/format'
import type { CardScope } from '../../credit'
import { textKeys, useLoans, type LoanKind, type LoanPerms } from '../../loans'
import { BankLogo } from '../banks/BankLogo'
import { LoanDetailModal } from './LoanDetailModal'
import { LoanForm } from './LoanForm'

type Props = { kind?: LoanKind; scope: CardScope; perms: LoanPerms }

/**
 * Krediler / nakit avanslar: kalan anapara/borç, sonraki taksit, gecikmiş taksitler; detayda ödeme planı ve ödeme.
 */
export function LoansSection({ kind = 'loan', scope, perms }: Props) {
  const { t } = useTranslation()
  const k = (key: string) => textKeys(kind, key)
  const loans = useLoans(kind, scope)
  const [creating, setCreating] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const personal = scope === 'personal'

  return (
    <Section
      title={t(personal ? k('myTitle') : k('title'))}
      icon={Landmark}
      bodyClassName="p-0"
      actions={
        perms.create ? (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus />
            {t(k('add'))}
          </Button>
        ) : null
      }
    >
      {loans.isError ? (
        <ErrorState error={loans.error} onRetry={() => void loans.refetch()} />
      ) : loans.isLoading ? (
        <Skeleton className="m-4 h-32" />
      ) : !loans.data?.length ? (
        <EmptyState
          icon={Landmark}
          title={t(k('empty'))}
          description={t(personal ? k('personalHint') : k('companyHint'))}
        />
      ) : (
        <ul className="divide-y">
          {loans.data.map((l) => (
            <li key={l.id} className="flex flex-wrap items-start gap-x-4 gap-y-3 px-4 py-3">
              <div className="min-w-0 flex-1 basis-56 space-y-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                  <BankLogo url={l.bankLogoUrl} name={l.bankName} />
                  {l.name}
                  {l.repayment === 'bullet' ? <Badge tone="neutral">{t('finance.cashAdvances.bullet')}</Badge> : null}
                  {l.status === 'closed' ? <Badge tone="success">{t(k('closed'))}</Badge> : null}
                  {l.overdueCount > 0 ? (
                    <Badge tone="danger">{t(k('overdue'), { count: l.overdueCount })}</Badge>
                  ) : null}
                </p>
                <p className="text-muted-foreground text-xs">
                  {l.bankName ? `${l.bankName} · ` : ''}
                  {fmtMoney(l.principal)} · {t(k('rateShort'), { rate: l.monthlyRate })} ·{' '}
                  {l.repayment === 'bullet'
                    ? t('finance.cashAdvances.dueOn', { date: fmtDate(l.firstDueDate) })
                    : t(k('progress'), { paid: l.paidCount, total: l.installmentCount })}
                </p>
                {l.nextDueDate ? (
                  <p className="text-muted-foreground flex items-center gap-1 text-xs">
                    <CalendarClock className="size-3.5" aria-hidden />
                    {t(k('next'), { date: fmtDate(l.nextDueDate), amount: fmtMoney(l.nextDueAmount) })}
                  </p>
                ) : null}
              </div>
              <dl className="grid min-w-[14rem] grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <dt className="text-muted-foreground">{t(k('remainingPrincipal'))}</dt>
                <dt className="text-muted-foreground">{t(k('remainingTotal'))}</dt>
                <dd className="text-sm font-semibold tabular-nums">{fmtMoney(l.remainingPrincipal)}</dd>
                <dd className="text-sm tabular-nums">{fmtMoney(l.remainingTotal)}</dd>
              </dl>
              <Button size="sm" variant="outline" onClick={() => setOpenId(l.id)}>
                <ListOrdered />
                {t(k('schedule'))}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {creating ? (
        <LoanForm
          kind={kind}
          scope={scope}
          onDone={(id) => {
            setCreating(false)
            if (id) setOpenId(id)
          }}
        />
      ) : null}
      {openId ? <LoanDetailModal kind={kind} id={openId} perms={perms} onClose={() => setOpenId(null)} /> : null}
    </Section>
  )
}
